import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { z } from 'zod';

import { Database, Json, withLogs } from '@core';

import { apiClient, ApiConfig } from '../apiClient';

import { CustomCorridor } from './customCorridors';
import { LayoutCustomization } from './layoutCustomizations';

export const LAYOUT_TYPE = {
    PUBLIC: 'public',
    PRIVATE: 'private',
    DEFAULT: 'default',
} as const;

export type LayoutType = (typeof LAYOUT_TYPE)[keyof typeof LAYOUT_TYPE];

export type Layout = {
    id: number;
    ownerUserId: string;
    name: string;
    layoutType: string;
    clubTypeId?: number | null;
};

export type FullLayout = Layout & {
    customizations: LayoutCustomization[];
    customCorridors: CustomCorridor[];
};

export type LastUsedLayoutParams = {
    clubId?: number;
    limit?: number;
};

const layoutSchema = z.object({
    id: z.number().optional(),
    ownerUserId: z.string(),
    name: z.string().min(3).max(200),
    layoutType: z.union([z.literal('public'), z.literal('private')]),
});

export const fetchLayouts = withLogs(async (layoutTypes?: LayoutType[]) => {
    const { data } = await apiClient.get<Layout[]>(ApiConfig.path(`api/v1/layouts`), {
        params: { layout_types: layoutTypes },
    });
    return data;
}, 'fetchLayouts');

export const fetchLayoutById = withLogs(async (layoutId: number) => {
    const { data } = await apiClient.get<FullLayout>(ApiConfig.path(`api/v1/layouts/${layoutId}`));
    return data;
}, 'fetchLayoutById');

export const fetchLayoutsByUser = withLogs(
    async (supabaseClient: SupabaseClient<Database>, userId: string, layoutTypes?: LayoutType[]): Promise<Layout[]> => {
        const { data, error } = await supabaseClient
            .from('layouts')
            .select('*')
            .eq('owner_user_id', userId)
            .in('layout_type', layoutTypes ?? [LAYOUT_TYPE.PRIVATE, LAYOUT_TYPE.PUBLIC, LAYOUT_TYPE.DEFAULT]);
        if (error) {
            throw error;
        }

        const converted = camelcaseKeys(data ?? [], { deep: true });
        return converted;
    },
    'fetchLayoutsByUser',
);

export const getLatestUsedLayouts = withLogs(async (lastUsedParams?: LastUsedLayoutParams) => {
    const { data } = await apiClient.get<Layout[]>(ApiConfig.path(`api/v1/layouts/preferences`), {
        params: snakecaseKeys(lastUsedParams ?? {}),
    });
    return data;
}, 'getLatestUsedLayouts');

export const upsertLayout = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        userId: string,
        layout: Layout | Omit<Layout, 'id'>,
    ): Promise<Layout> => {
        const valid = layoutSchema.parse({ ...layout, ownerUserId: userId });
        const payload = snakecaseKeys(valid, { deep: true });

        const { data, error } = await supabaseClient.from('layouts').upsert(payload).select().single();

        if (error) {
            throw error;
        }

        return camelcaseKeys(data, { deep: true });
    },
    'upsertLayout',
);

export type LayoutUpdate = {
    layoutId: number;
    customizations: {
        floor: string[];
        kiosk: string[];
    };
    name: string;
    keyParameterId: string;
    // owner of the layout, if it differs from currently logged in user
    userId?: string;
    modifications?: {
        floor?: {
            disabledGraphLines?: string[];
        };
    };
    metadata?: {
        [moduleId: string]: Json;
    };
};

export const updateLayout = withLogs(
    async (supabaseClient: SupabaseClient<Database>, layoutUpdate: LayoutUpdate): Promise<FullLayout | null> => {
        const { data: response, error } = await supabaseClient.functions.invoke<{ data: FullLayout }>('layout-update', {
            body: layoutUpdate,
        });
        if (error || !response?.data) {
            throw error || new Error('No layout returned');
        }

        return response.data;
    },
    'updateLayout',
);

export type LayoutCreate = {
    sourceLayoutId?: number;
    name: string;
    userId?: string;
    clubTypeId?: number;
};

export const createLayout = withLogs(
    async (supabaseClient: SupabaseClient<Database>, layoutCreate: LayoutCreate): Promise<FullLayout | null> => {
        const { data: response, error } = await supabaseClient.functions.invoke<{ data: FullLayout }>('layout-create', {
            body: layoutCreate,
        });
        if (error || !response?.data) {
            throw error || new Error('No layout returned');
        }

        return response.data;
    },
    'createLayout',
);
export const deleteLayout = withLogs(
    async (supabaseClient: SupabaseClient<Database>, userId: string, layoutId: number) => {
        const { error } = await supabaseClient.from('layouts').delete().eq('owner_user_id', userId).eq('id', layoutId);

        if (error) {
            throw error;
        }
    },
    'deleteLayout',
);

export const subscribeToLayout = (supabase: SupabaseClient<Database>, layoutId: number, handler: () => void) => {
    return supabase
        .channel('layouts')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'layouts',
                filter: `id=eq.${layoutId}`,
            },
            (payload) => {
                try {
                    handler();
                } catch (error) {
                    console.error('Error processing change:', error);
                }
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('user settings subscription error:', status);
            }
        });
};
